import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { FileCheck2, FileUp } from "lucide-react";
import { Badge } from "catalyst-ui-kit/badge";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
} from "components/modalx/modalx.slice";
import { btnLinkCls, btnSlimPrimaryCls, cardCls, helpTextCls } from "components/tw/classes";
import { vilniusYmd } from "utils/vilnius-date";
import { Order } from "types/Order";

type Props = {
  companyId: string;
  orderEntity: Order;
  /** Where the attached PDF downloads from, when there is one. */
  downloadUrl?: string;
  /** Master or Signature — attaching asserts a signature made elsewhere. */
  canAttach: boolean;
  /** Refetch the order after a change. */
  onChanged: () => void;
};

/**
 * The order page's document step for an external contract. It stands in for
 * "generate the document" and "sign it": the contract was drawn up and signed
 * outside the system, so the only thing to do here is attach its signed PDF —
 * or replace it while nothing has been sent for signing.
 */
export default function ExternalDocumentBox({
  companyId,
  orderEntity,
  downloadUrl,
  canAttach,
  onChanged,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mainFile = orderEntity.files?.[0];
  const hasDocument = !!mainFile;
  const contractDate = vilniusYmd(orderEntity.date);

  const openAttach = () => {
    dispatch(
      setModalxOptions({
        title: hasDocument
          ? t("replace_external_document")
          : t("attach_external_document"),
      })
    );
    dispatch(setModalxComponent("AttachExternalDocument"));
    dispatch(
      setModalxComponentProps({
        companyId,
        orderId: orderEntity.id,
        externalNumber: orderEntity.externalNumber,
        contractDate,
        hasDocument,
        onDone: onChanged,
      })
    );
  };

  return (
    <div className="card-wrapper">
      <div className={`card-box ${mainFile?.isSigned ? "border-green" : "border-yellow"}`}>
        <div className={cardCls}>
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              {t("external_contract_document")}
            </h2>
            {mainFile?.isSigned ? (
              <Badge color="green">{t("signed")}</Badge>
            ) : null}
          </div>

          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            <dt className="text-zinc-500">{t("order_external_number")}</dt>
            <dd className="font-medium">{orderEntity.externalNumber || "—"}</dd>
            <dt className="text-zinc-500">{t("external_contract_date")}</dt>
            <dd>{contractDate || "—"}</dd>
          </dl>

          {hasDocument && downloadUrl ? (
            <a
              href={downloadUrl}
              target="_blank"
              rel="noreferrer"
              className={`${btnLinkCls} mt-3`}
            >
              <FileCheck2 className="size-4" aria-hidden="true" />
              <span className="truncate">{mainFile?.name || mainFile?.title}</span>
            </a>
          ) : (
            <p className={helpTextCls}>{t("external_document_missing")}</p>
          )}

          <div className="mt-3 flex justify-center">
            {canAttach ? (
              <button type="button" className={btnSlimPrimaryCls} onClick={openAttach}>
                <FileUp className="size-3.5" aria-hidden="true" />
                {hasDocument
                  ? t("replace_external_document")
                  : t("attach_external_document")}
              </button>
            ) : (
              <p className={helpTextCls}>{t("external_document_forbidden")}</p>
            )}
          </div>
        </div>
      </div>
      <div className="connector-wrapper">
        <div className="vertical-line"></div>
        <div className="arrow-down"></div>
      </div>
    </div>
  );
}
